/**
 * PATH: packages/job-hub/src/jobs/qa/useQaPlanPreview.ts
 * FILE_ID: JOBHUB-QA-PREVIEW-HOOK-001
 * PURPOSE: Load accepted-quote QA previews and detect same-hash inspections.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  findDuplicateQaInspection,
  type QaApi,
  type QaCreateMode,
  type QaInspectionSummary,
} from './qaApi'

export function useQaPlanPreview({
  api,
  quoteSheetIds,
  mode,
  inspections,
  explicitlyIncludedSourceIds = [],
}: {
  api: QaApi
  quoteSheetIds: number[]
  mode: QaCreateMode
  inspections: QaInspectionSummary[]
  explicitlyIncludedSourceIds?: string[]
}) {
  const [reviewOpen, setReviewOpen] = useState(false)
  const idsKey = quoteSheetIds.join(',')
  const includeKey = explicitlyIncludedSourceIds.join(',')

  const query = useQuery({
    queryKey: ['qa-plan-preview', idsKey, mode, includeKey],
    queryFn: () => api.previewQaPlan(quoteSheetIds, mode, explicitlyIncludedSourceIds),
    enabled: reviewOpen && quoteSheetIds.length > 0,
    retry: false,
  })

  const duplicate = useMemo(() => {
    for (const plan of query.data?.plans ?? []) {
      const match = findDuplicateQaInspection(inspections, plan.source_hash)
      if (match) return match
    }
    return undefined
  }, [inspections, query.data])

  return {
    reviewOpen,
    openReview: () => setReviewOpen(true),
    closeReview: () => setReviewOpen(false),
    preview: query.data,
    previewLoading: query.isLoading,
    previewError: query.error,
    duplicate,
  }
}
